import { Injectable, signal } from "@angular/core";
import { IContentLocation, IReaderViewAnnotation, IReaderViewAnnotationOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { BaseEpubAnnotationService } from "./base-epub-annotation.service";
import { DocumentManagerService } from "../document/document-manager.service";
import { BehaviorSubject } from "rxjs";
import { textNotesFonts } from "src/app/core/common";
import { EpubStrokeColor } from "src/app/core/epub-utils/enums";
import { ITextNoteFont } from "src/app/core/interfaces";
import { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { CommandInvokerService } from "../command/command-invoker.service";

export interface IEpubCommentData {
    commentId: number;
    text: string;
    heightPx: number;
    widthPx: number;
    fontFamily: ITextNoteFont;
    fontSize: number;
    fontColor: EpubStrokeColor;
    highlightColor: string;
}

@Injectable({
    providedIn: 'root'
})
export class EpubCommentService extends BaseEpubAnnotationService<IEpubCommentData> {
    public readonly LAYER_NAME = 'comments';
    public focusedComment$ = new BehaviorSubject<IReaderViewAnnotation<IEpubCommentData> | undefined>(undefined);
    public mouseIsOverEditor = signal(false);

    public readonly defaultText = '';
    public readonly defaultHeightPx = 200;
    public readonly defaultWidthPx = 200;
    public readonly defaultFontFamily = textNotesFonts[0];
    public readonly defaultFontSize = 12;
    public readonly defaultFontColor = EpubStrokeColor.Black;
    public readonly defaultHighlightColor = '#FFFF00';

    constructor(documentManagerService: DocumentManagerService, commandInvoker: CommandInvokerService) {
        super(documentManagerService, commandInvoker);
    }

    protected createAnnotation(locator: ILocator, data: IEpubCommentData) {
        this.createComment(locator, data);
    }

    public getComments(): IReaderViewAnnotation<IEpubCommentData>[] {
        if (!this.annotationLayer) return [];
        return this.annotationLayer.getAnnotations().filter((data) => data.getCustomData() != null);
    }

    public openEditor(comment: IReaderViewAnnotation<IEpubCommentData>) {
        // NOTE 10 DEC 2025
        // delay of 100ms because when you have an annotation on line 1 and an annotation on line 2
        // and you move the mouse quickly from line 1 to line 2, the pointerleave event of line 1
        // fires after the pointerenter event of line 2
        setTimeout(() => {
            if (!this.focusedComment$.value) {
                this.focusedComment$.next(comment);
            }
        }, 100);
    }

    public annotationPointerLeave() {
        // first add a delay of 100ms
        // because we don't want this to trigger right before the comment editor mouse leave
        setTimeout(() => {
            // check if the editor is open, if it's not, remove comment
            if (!this.mouseIsOverEditor()) {
                this.focusedComment$.next(undefined);
            }
        }, 0);
    }

    public openNewCommentEditor(location: IContentLocation) {
        const annotationLayer = this.annotationLayer;
        if (!annotationLayer || !this.docId) return;

        const commentId = this.getHighestCommentId() + 1;
        const data = {
            commentId: commentId,
            text: this.defaultText,
            heightPx: this.defaultHeightPx,
            widthPx: this.defaultWidthPx,
            fontFamily: this.defaultFontFamily,
            fontSize: this.defaultFontSize,
            fontColor: this.defaultFontColor,
            highlightColor: this.defaultHighlightColor
        } as IEpubCommentData;

        const comment = this.createComment(location, data);

        setTimeout(() => {
            this.focusedComment$.next(comment);
        }, 0);
    }

    public updateFocusedCommentData(data: Partial<IEpubCommentData>) {
        const focusedCommentId = this.focusedComment$.value?.getCustomData()?.commentId;
        if (!focusedCommentId) return;

        const comments = this.annotationLayer?.getAnnotations();
        if (!comments) return;

        const commentToUpdate = comments.find(note => note.getCustomData()?.commentId === focusedCommentId);
        if (commentToUpdate && commentToUpdate.getCustomData()) {
            commentToUpdate.setCustomData({ ...commentToUpdate.getCustomData()!, ...data });
            this.updateCommentHighlight(commentToUpdate);
        }
    }

    public deleteFocusedComment() {
        const focusedCommentId = this.focusedComment$.value?.getCustomData()?.commentId;
        if (!focusedCommentId) return;

        this.deleteCommentById(focusedCommentId);
    }

    public deleteCommentById(id: number) {
        const comments = this.annotationLayer?.getAnnotations();
        if (!comments) return;

        const commentToDelete = comments.find(note => note.getCustomData()?.commentId === id);
        if (commentToDelete) {
            this.annotationLayer?.destroyAnnotation(commentToDelete);
            this.emitDocChanged(this.docId!);
        }
    }

    public getCommentHtmlElement(comment: IReaderViewAnnotation<IEpubCommentData>): HTMLElement | null {
        const data = comment.getCustomData()!;
        if (!this.docId) return null;

        return document.querySelector(`.${this.getCommentClassName(data.commentId, this.docId)}`) as HTMLElement;
    }

    private createComment(location: IContentLocation | ILocator, data: IEpubCommentData): IReaderViewAnnotation<IEpubCommentData> {
        const className = this.getCommentClassName(data.commentId, this.docId!);
        const comment = this.annotationLayer!.createAnnotation<IEpubCommentData>(location)

        comment.setOptions({ containerClassName: className });
        comment.setCustomData(data);
        this.updateCommentHighlight(comment);

        this.emitDocChanged(this.docId!);
        return comment;
    }

    private getHighestCommentId(): number {
        const comments = this.annotationLayer?.getAnnotations();

        if (!comments || comments.length === 0) {
            return 0;
        }

        const highest = comments.reduce((prev, curr) => {
            const prevId = prev.getCustomData()?.commentId ?? 0;
            const currId = curr.getCustomData()?.commentId ?? 0;
            return currId > prevId ? curr : prev;
        }, comments[0]);

        return highest.getCustomData()?.commentId ?? 0;
    }

    private updateCommentHighlight(comment: IReaderViewAnnotation<IEpubCommentData>) {
        const data = comment.getCustomData();
        if (data && data.highlightColor) {
            comment.setOptions(this.getCommentHighlightOptionStyle(data.highlightColor));
            this.emitDocChanged(this.docId!);
        }
    }

    private getCommentClassName(noteNumber: number, docId: string) {
        return `epub-comment-${noteNumber}-${docId}`;
    }

    private getCommentHighlightOptionStyle(color: string): IReaderViewAnnotationOptions {
        return {
            rangeStyle: {
                'border-bottom': '3px solid',
                'border-bottom-color': color,
            }
        }
    }
}